/**
 * German operator-facing labels for raw enum values from the API.
 * Unknown values fall back to the raw key so a future enum extension
 * degrades readable, not broken. Case intents and subject types live in
 * caseLabels.ts.
 */

const OUTCOME: Record<string, string> = {
  resolved: "Gelöst",
  escalated: "Eskaliert",
  abandoned: "Abgebrochen",
  emergency: "Notfall",
};

const IDENTITY: Record<string, string> = {
  unknown: "Unbekannt",
  ani_match: "Rufnummer bekannt",
  pin_verified: "PIN geprüft",
  staff: "Mitarbeiter",
};

const TAKEOVER: Record<string, string> = {
  none: "Agent führt",
  listening: "Operator hört mit",
  taken_over: "Operator übernommen",
};

const PRIORITY: Record<string, string> = {
  normal: "Normal",
  high: "Hoch",
  hoch: "Hoch",
  emergency: "Dringend",
  dringend: "Dringend",
};

const CALLBACK_STATUS: Record<string, string> = {
  pending: "Ausstehend",
  attempted: "Versucht",
  completed: "Erledigt",
  failed: "Fehlgeschlagen",
  cancelled: "Storniert",
};

const CASE_STATUS: Record<string, string> = {
  draft: "ENTWURF",
  human_reviewed: "Geprüft",
};

const HANDOFF: Record<string, string> = {
  vertriebsinnendienst: "Auftragsanlage",
  technik_aftersales: "Technik",
  planung: "Planung",
  reklamation_retoure: "Reklamation/Service",
  regional_partner: "Regionalpartner",
  download_dokument: "Download / Dokument",
  operator: "Operator",
  none: "Keines",
};

const HANDOFF_REASON: Record<string, string> = {
  deterministic_emergency: "Deterministischer Notfallpfad",
  caller_requested: "Vom Anrufer gewünscht",
  auto_escalated: "Automatisch eskaliert",
  intent_routing: "Nach Anliegen zugeordnet",
};

/** Machine-detected gaps in a case draft (apps/voice/processors/case_qualification.py). */
const MISSING_FIELD: Record<string, string> = {
  email_recipient: "E-Mail-Empfänger",
  email_subject: "E-Mail-Betreff",
  email_body: "E-Mail-Text",
};

const HEALTH: Record<string, string> = {
  ok: "Bereit",
  degraded: "Eingeschränkt",
  down: "Ausgefallen",
  unconfigured: "Nicht konfiguriert",
  configured: "Konfiguriert",
  misconfigured: "Fehlkonfiguriert",
  error: "Fehler",
  unknown: "Unbekannt",
};

const SERVICE: Record<string, string> = {
  database: "Datenbank",
  nats: "Ereignisbus (NATS)",
  llm: "Sprachmodell (LLM)",
  tts: "Sprachausgabe (TTS)",
  asr: "Spracherkennung (STT)",
  ragflow: "Wissensbasis (RAGFlow)",
  pim: "Produktdaten (PIM)",
  cerbos: "Autorisierung (Cerbos)",
  docmost: "Wissensredaktion (Docmost)",
};

const SYNC: Record<string, string> = {
  unconfigured: "Nicht konfiguriert",
  never_applied: "Gespeichert, nie angewendet",
  pending: "Änderung ausstehend",
  live: "Live",
};

const EMERGENCY: Record<string, string> = {
  gas_smell_or_gas_leak: "Gasgeruch / Gasleck",
  fire_or_smoke: "Feuer / Rauch",
  co_alarm: "CO-Alarm",
  water_leak: "Wasserschaden",
  electrical_hazard: "Elektrische Gefahr",
};

function pick(map: Record<string, string>, value: string | null | undefined, fallback = "–"): string {
  if (!value) return fallback;
  return map[value] ?? value;
}

export const outcomeLabel = (v: string | null | undefined) => pick(OUTCOME, v, "Offen");
export const identityLabel = (v: string | null | undefined) => pick(IDENTITY, v, "Unbekannt");
export const takeoverLabel = (v: string | null | undefined) => pick(TAKEOVER, v, "Agent führt");
export const priorityLabel = (v: string | null | undefined) => pick(PRIORITY, v, "Normal");
export const callbackStatusLabel = (v: string | null | undefined) => pick(CALLBACK_STATUS, v);
export const caseStatusLabel = (v: string | null | undefined) => pick(CASE_STATUS, v, "Kein Entwurf");
export const handoffLabel = (v: string | null | undefined) => pick(HANDOFF, v);
export const handoffReasonLabel = (v: string | null | undefined) => pick(HANDOFF_REASON, v);
export const missingFieldLabel = (v: string) => MISSING_FIELD[v] ?? v;
export const healthLabel = (v: string | null | undefined) => pick(HEALTH, v, "Unbekannt");
export const serviceLabel = (v: string | null | undefined) => pick(SERVICE, v);
export const syncLabel = (v: string | null | undefined) => pick(SYNC, v);
export const emergencyLabel = (v: string | null | undefined) => pick(EMERGENCY, v);

/** Badge tone for an outcome value (color never carries meaning alone). */
export function outcomeTone(v: string | null | undefined): "ok" | "warn" | "danger" | "neutral" {
  switch (v) {
    case "resolved":
      return "ok";
    case "escalated":
    case "abandoned":
      return "warn";
    case "emergency":
      return "danger";
    default:
      return "neutral";
  }
}

/**
 * Label and tone of one health component. A component another service owns
 * (scope "external") is not configured *here* by design, so it reads
 * "Nicht in diesem Dienst" in neutral instead of a warning.
 */
export function serviceStatus(service: {
  status: string;
  scope?: "service" | "external";
}): { label: string; tone: ReturnType<typeof healthTone> } {
  if (service.scope === "external" && service.status === "unconfigured") {
    return { label: "Nicht in diesem Dienst", tone: "neutral" };
  }
  return { label: healthLabel(service.status), tone: healthTone(service.status) };
}

/** Badge tone for a health/service status. */
export function healthTone(v: string | null | undefined): "ok" | "warn" | "danger" | "neutral" {
  switch (v) {
    // "configured" ist in Ordnung: eine aus der Konfiguration aufgeloeste
    // Strecke. Der Unterschied zur Messung steht im Wortlaut, nicht in der Farbe.
    case "ok":
    case "configured":
      return "ok";
    case "degraded":
    case "unconfigured":
      return "warn";
    case "down":
    case "error":
    case "misconfigured":
      return "danger";
    default:
      return "neutral";
  }
}

/**
 * One knowledge gap status, named the same on every page. The gaps page and
 * the analytics page each had their own map, so the same value read
 * "Gelöst" in one place and "Erledigt" in the other.
 */
export function gapStatusMeta(status: string): { label: string; badgeClass: string } {
  switch (status) {
    case "open":
      return { label: "Offen", badgeClass: "badge badge-warn" };
    case "resolved":
      return { label: "Gelöst", badgeClass: "badge badge-ok" };
    case "dismissed":
      return { label: "Verworfen", badgeClass: "badge badge-accent" };
    default:
      return { label: status, badgeClass: "badge" };
  }
}

/**
 * PIM connection status. The PIM page and the connections card read the same
 * field; only this map knows the PIM-specific values such as auth_error.
 */
export function pimStatusLabel(status: string): string {
  switch (status) {
    case "ok":
      return "Verbunden";
    case "auth_error":
      return "Anmeldung fehlgeschlagen";
    case "unconfigured":
      return "Nicht konfiguriert";
    case "error":
      return "Fehler";
    default:
      return healthLabel(status);
  }
}

/** Selectable roles of a stored LLM connection, in display order. */
export const LLM_ROLES = ["primary", "fallback", "judge"] as const;

/** Role of a stored LLM connection. */
export function llmRoleLabel(role: string): string {
  switch (role) {
    case "primary":
      return "Primär";
    case "fallback":
      return "Fallback";
    case "judge":
      return "Bewertung (judge)";
    default:
      return role;
  }
}

/* ------------------------------------------------------------------ */
/* Leitstand: Begriffe für Fachanwender. Keine internen Wörter wie     */
/* Provider, Intake, Quarantäne, Evidenz, Pipeline, Mandant, Claim.    */
/* ------------------------------------------------------------------ */

const TOOL: Record<string, string> = {
  product_lookup: "Artikel nachschlagen",
  lookup_product_by_article: "Artikel nachschlagen",
  product_search: "Produktsuche",
  search_products: "Produktsuche",
  search_knowledge: "Wissenssuche",
  specialist_answer: "Fachauskunft",
  send_email: "Mailversand",
  ingest_case: "Gesprächsprotokoll übernehmen",
  request_callback: "Rückruf anlegen",
  pim_diagnostics: "Produktdaten prüfen",
};

/** Werkzeug eines Aufrufs; akzeptiert den Ereignistyp mit Präfix "machine_tool:". */
export function toolLabel(tool: string): string {
  const key = tool.startsWith("machine_tool:") ? tool.slice("machine_tool:".length) : tool;
  return TOOL[key] ?? key.replace(/_/g, " ");
}

const MAIL_LANE: Record<string, string> = {
  machine_tools: "Mails des Telefonassistenten",
  elevenlabs_tools: "Mails der eigenen Sprachstrecke",
  callback_notice: "Rückrufmails an Abteilungen",
};

export const mailLaneLabel = (lane: string) => MAIL_LANE[lane] ?? lane.replace(/_/g, " ");

const STOERUNG_ART: Record<string, string> = {
  import: "Import",
  mail: "Mailversand",
  werkzeug: "Werkzeug",
  komponente: "Komponente",
};

export const stoerungArtLabel = (art: string) => STOERUNG_ART[art] ?? art;

const KOMPONENTE: Record<string, string> = {
  db: "Datenbank",
  cerbos: "Rechteprüfung",
  llm: "Sprachmodell",
  ragflow: "Wissensbasis",
  pim_cache: "Produktdaten-Zwischenspeicher",
};

export const komponenteLabel = (key: string) => KOMPONENTE[key] ?? key;

const HERKUNFT: Record<string, string> = {
  telefonassistent: "vom Telefonassistenten",
  gespraechsprotokoll: "aus dem Gesprächsprotokoll",
  altbestand: "aus dem Altbestand",
};

export const herkunftLabel = (herkunft: string) => HERKUNFT[herkunft] ?? herkunft;

/** Abteilungsschlüssel aus `callback_requests.department` (shared/machine_intake). */
const ABTEILUNG: Record<string, string> = {
  technik: "Technik",
  sales: "Auftragsanlage",
  reklamation: "Reklamation",
  operator: "Telefonzentrale",
};

/** Abteilung eines Rückrufs; ältere Rohwerte wie "technik_aftersales" fallen auf die Übergabeliste zurück. */
export function abteilungLabel(abteilung: string | null | undefined): string {
  if (!abteilung) return "ohne Abteilung";
  return ABTEILUNG[abteilung] ?? HANDOFF[abteilung] ?? abteilung;
}

/** Ruhender Bereich: ein Satz, überall gleich. */
export const RUHT_HINWEIS = "Ruht seit 23.09.2026, IONOS nimmt die Anrufe an";

/**
 * Die eigene Sprachstrecke ruht. Solange das gilt, öffnen Live und Tests
 * keine Ereignisströme und folgen keinem Lauf im Takt.
 */
export const SPRACHSTRECKE_RUHT = true;
